<template>
  <div class="quota-bar">
    <div class="flex items-center justify-between mb-2">
      <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
        {{ label }}
      </span>
      <span class="text-sm text-gray-500 dark:text-gray-400">
        {{ formattedCurrent }} / {{ formattedLimit }}
      </span>
    </div>

    <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
      <div
        class="h-full rounded-full transition-all duration-300 ease-out"
        :class="[progressColorClass, { 'animate-pulse': isAnimating }]"
        :style="{ width: `${Math.min(percentage, 100)}%` }"
      />
    </div>

    <div class="flex items-center justify-between mt-1">
      <span class="text-xs text-gray-500 dark:text-gray-400">
        {{ percentage.toFixed(1) }}% used
      </span>
      <span v-if="timeToReset" class="text-xs text-gray-500 dark:text-gray-400">
        Resets {{ timeToReset }}
      </span>
    </div>

    <!-- Warning message for high usage -->
    <div
      v-if="percentage >= 90"
      class="mt-2 flex items-center text-xs"
      :class="{
        'text-warning-600 dark:text-warning-400': percentage >= 90 && percentage < 100,
        'text-error-600 dark:text-error-400': percentage >= 100,
      }"
    >
      <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
        <path
          fill-rule="evenodd"
          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
          clip-rule="evenodd"
        />
      </svg>
      <span v-if="percentage >= 100"> Quota exceeded - some features may be limited </span>
      <span v-else> Approaching quota limit </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

export interface QuotaBarProps {
  label: string;
  current: number;
  limit: number;
  color?: 'blue' | 'primary' | 'purple' | 'green' | 'yellow' | 'red';
  formatValue?: (value: number) => string;
  resetTime?: string | Date;
  animated?: boolean;
}

const props = withDefaults(defineProps<QuotaBarProps>(), {
  color: 'primary',
  formatValue: (value: number) => value.toString(),
  animated: false,
});

const isAnimating = ref(false);

// Computed properties
const percentage = computed(() => {
  if (props.limit === 0) return 0;
  return (props.current / props.limit) * 100;
});

const formattedCurrent = computed(() => {
  return props.formatValue(props.current);
});

const formattedLimit = computed(() => {
  return props.formatValue(props.limit);
});

const progressColorClass = computed(() => {
  const baseClasses = {
    blue: 'bg-blue-500',
    primary: 'bg-primary-500',
    purple: 'bg-purple-500',
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
  };

  // Override color based on usage percentage
  if (percentage.value >= 100) {
    return 'bg-red-500';
  } else if (percentage.value >= 90) {
    return 'bg-yellow-500';
  } else if (percentage.value >= 75) {
    return 'bg-orange-500';
  }

  return baseClasses[props.color] || baseClasses.primary;
});

const timeToReset = computed(() => {
  if (!props.resetTime) return null;

  const resetDate = new Date(props.resetTime);
  const now = new Date();
  const diffMs = resetDate.getTime() - now.getTime();

  if (diffMs <= 0) return null;

  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (diffHours > 24) {
    const diffDays = Math.floor(diffHours / 24);
    return `in ${diffDays}d`;
  } else if (diffHours > 0) {
    return `in ${diffHours}h ${diffMinutes}m`;
  } else {
    return `in ${diffMinutes}m`;
  }
});

// Watch for changes to trigger animation
watch(
  () => props.current,
  (newValue, oldValue) => {
    if (props.animated && newValue !== oldValue) {
      isAnimating.value = true;
      setTimeout(() => {
        isAnimating.value = false;
      }, 300);
    }
  }
);
</script>

<style scoped>
.quota-bar {
  @apply w-full;
}

/* Smooth progress bar animation */
.h-full {
  transition: width 0.3s ease-out;
}

/* Pulse animation for high usage */
@keyframes pulse-warning {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

.animate-pulse {
  animation: pulse-warning 2s infinite;
}

/* Accessibility improvements */
@media (prefers-reduced-motion: reduce) {
  .h-full {
    transition: none;
  }

  .animate-pulse {
    animation: none;
  }
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .bg-gray-200 {
    @apply border border-gray-400;
  }

  .dark .bg-gray-700 {
    @apply border border-gray-500;
  }
}
</style>
